import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useMatch } from 'react-router';
import { ROUTES, getRouteTitle } from '../../../../../constants/RouteNameMapping';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { clientConfigFieldsAtom } from '../../../../../services/state/AppConfigStateJ';
import { templateAssignmentsByCompanyIdStateJ } from '../../../../../services/state/KPI/KPICompaniesStateJ';
import { kpiRequestsCountStateJ } from '../../../../../services/state/KPI/KPIRequestsStateJ';
import { MaggieFeatureFlags } from '../../../../../util/feature-flags';
import { SubSectionNavigation } from '../../SubSectionNavigation/SubSectionNavigation';
import SubSectionNavItems, { NavItemType } from '../../SubSectionNavigation/SubSectionNavItems';
import { NavigationWrapper } from '../NavigationWrapper';
import { CustomTemplateNav } from './CustomTemplateNav';
import { DCConfigurationNav } from './DCConfigurationNav';
import { KPITemplatesSubNavigation } from './KPITemplatesSubNavigation';

export const KPINavigation = () => {
  const match = useMatch(`/${ROUTES.KPI}/*`);
  const nAssigned = useAtomValue(templateAssignmentsByCompanyIdStateJ)?.size;
  const reqResCount = useAtomValue(kpiRequestsCountStateJ);
  const notSentCount = reqResCount?.notSent;
  const toReviewCount = reqResCount?.submitted;
  const permissionService = PermissionService.get();
  const usingConsolidatedDataCollection =
    useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection ?? false;
  const { useStarbreakerCollectionV2 } = useFlags<MaggieFeatureFlags>();

  const sectionsList = useMemo(() => {
    const res: NavItemType[] = [
      {
        id: ROUTES.COMPANIES,
        value: getRouteTitle(ROUTES.COMPANIES),
        badgeIndicator: usingConsolidatedDataCollection ? undefined : nAssigned,
      },
    ];

    if (usingConsolidatedDataCollection) {
      res.unshift({
        id: ROUTES.KPI_MANAGE,
        value: 'Manage Requests',
      });
    }

    if (!usingConsolidatedDataCollection) {
      res.unshift(
        {
          id: ROUTES.KPI_REQUESTS,
          value: getRouteTitle(ROUTES.KPI_REQUESTS),
          badgeIndicator: notSentCount,
        },
        {
          id: ROUTES.KPI_RESPONSES,
          value: getRouteTitle(ROUTES.KPI_RESPONSES),
          badgeIndicator: toReviewCount,
        }
      );
    }

    return res;
  }, [notSentCount, toReviewCount, usingConsolidatedDataCollection, nAssigned]);

  if (match === null || !permissionService.hasPermission(PermissionKey.canReadDataCollection)) {
    return null;
  }

  return (
    <NavigationWrapper title='Data Collection'>
      <SubSectionNavigation title='Management'>
        <SubSectionNavItems sectionList={sectionsList} parentRoute='kpis' />
      </SubSectionNavigation>

      {!usingConsolidatedDataCollection && <KPITemplatesSubNavigation />}
      {usingConsolidatedDataCollection && !useStarbreakerCollectionV2 && <DCConfigurationNav />}
      {usingConsolidatedDataCollection && useStarbreakerCollectionV2 && <CustomTemplateNav />}
    </NavigationWrapper>
  );
};
